import React, { useMemo, useState } from 'react';
import { Pin, Loader2, Siren } from 'lucide-react';
import { useMyTracking } from '../../lib/useMyTracking';
import TrackingRow, { liveMetrics } from './TrackingRow';

export default function MyTrackingPanel({ onSelect, tickPrices }) {
    const { data } = useMyTracking();
    const [tab, setTab] = useState('open');
    const { open, closed } = useMemo(() => {
        const items = data?.items || [];
        return { open: items.filter((i) => i.status === 'open'), closed: items.filter((i) => i.status !== 'open') };
    }, [data]);

    if (!data) {
        return (
            <div data-testid="my-tracking-loading" className="border border-slate-700 bg-slate-800 rounded-xl p-4 flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Takiplerin yükleniyor…
            </div>
        );
    }
    const list = tab === 'open' ? open : closed;
    const levels = open.map((i) => liveMetrics(i, tickPrices?.[i.symbol]?.price ?? i.current_price).warnLevel);
    const nearCount = levels.filter((l) => l >= 50).length;
    const warnCount = levels.filter((l) => l === 30).length;
    const totalUsd = open.reduce((acc, i) => acc + (liveMetrics(i, tickPrices?.[i.symbol]?.price ?? i.current_price).pnlUsd || 0), 0);

    return (
        <div data-testid="my-tracking-panel" className="border border-amber-500/25 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-3 flex-wrap">
                <Pin className="h-4 w-4 text-amber-300" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-100 font-semibold">Benim Takibim</span>
                <span className="text-[10px] text-slate-400">web'den "Takibe Al" + Telegram /aldim ile eklenenler</span>
                {open.length > 0 && (
                    <span data-testid="my-tracking-total-usd" className={`text-[11px] font-mono font-semibold ${totalUsd >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                        Açık K/Z: {totalUsd >= 0 ? '+' : '-'}${Math.abs(totalUsd).toFixed(2)}
                    </span>
                )}
                {nearCount > 0 && (
                    <span data-testid="my-tracking-near-stop-count" className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-rose-600 text-white animate-pulse">
                        <Siren className="h-3 w-3" /> {nearCount} STOPA YAKIN
                    </span>
                )}
                {warnCount > 0 && (
                    <span data-testid="my-tracking-warn-count" className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-400 text-slate-950">
                        {warnCount} DİKKAT
                    </span>
                )}
                <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                    <TabBtn active={tab === 'open'} onClick={() => setTab('open')} testid="my-tracking-tab-open">Açık ({open.length})</TabBtn>
                    <TabBtn active={tab === 'closed'} onClick={() => setTab('closed')} testid="my-tracking-tab-closed">Kapanan · 7 gün ({closed.length})</TabBtn>
                </span>
            </div>
            {list.length === 0 ? (
                <EmptyState tab={tab} />
            ) : (
                <div className="divide-y divide-slate-700/50 max-h-[460px] overflow-y-auto">
                    {list.map((item) => (
                        <TrackingRow key={item.id} item={item} onSelect={onSelect} tick={tickPrices?.[item.symbol]} />
                    ))}
                </div>
            )}
        </div>
    );
}

const TabBtn = ({ active, onClick, children, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`px-2 py-0.5 rounded-sm uppercase tracking-wider border transition-colors ${
            active ? 'bg-amber-500/20 text-amber-100 border-amber-500/40' : 'bg-slate-700 text-slate-300 border-slate-700 hover:text-slate-100'
        }`}
    >
        {children}
    </button>
);

const EmptyState = ({ tab }) => (
    <div data-testid="my-tracking-empty" className="px-4 py-6 text-xs text-slate-400 leading-relaxed">
        {tab === 'open' ? (
            <>
                Şu an takip ettiğin öneri yok. Bir RESMİ öneriyle pozisyon açtığında
                <span className="text-emerald-300 font-medium"> Detaylı analiz → Sistem Önerilerim </span>
                listesindeki <span className="text-emerald-300 font-medium">"Takibe Al"</span> butonuna bas ya da Telegram'da
                <code className="text-emerald-300"> /aldim ID 100 5x </code> yaz (teminat + kaldıraç). TP/Stop/yön değişiminde ve kaldıraçlı zarar teminatın %30 ve %50'sine ulaşınca kademeli uyarı gelir.
            </>
        ) : (
            'Son 7 günde kapanan takibin yok.'
        )}
    </div>
);
